/**
 * The scoped dashboard summary as a TanStack query: restored from the user's
 * IndexedDB cache (practice scope) and revalidated in the background;
 * invalidated (debounced) when activity lands on the stream or the client is
 * invalidated; refetched on window focus by the query client.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { useOnClientInvalidate } from "@/features/clientProfile/lib/clientInvalidation";
import { queryClient } from "@/lib/query/queryClient";
import { useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";

import { DashboardAPI } from "./api/dashboardApi";
import type {
  ClientDashboard,
  DashboardScope,
  PracticeDashboard
} from "./types";

export const SUMMARY_REFRESH_DEBOUNCE_MS = 2000;

type SummaryFor<S extends DashboardScope> = S["kind"] extends "client"
  ? ClientDashboard
  : PracticeDashboard;

export const summaryQueryKey = (
  kind: DashboardScope["kind"] | null,
  id: string | null,
  windowDays: number
) => ["dashboard", kind, id, windowDays] as const;

export type DashboardSummaryState<T> = {
  data: T | null;
  isLoading: boolean;
  /** A refetch is in flight while `data` is still showing. */
  isRefreshing: boolean;
  error: string | null;
  /**
   * Forced refresh: bypasses the server cache (`refresh=1`), writes the result
   * into the query cache. Rejects when the request fails (the data on screen
   * stays). Returns void for callers that only need a trigger.
   */
  refresh: () => Promise<void>;
  /** `as_of` of the payload on screen: when it was computed. */
  updatedAt: string | null;
};

export const useDashboardSummary = <S extends DashboardScope>(
  scope: S | null,
  { windowDays = 14 }: { windowDays?: number } = {}
): DashboardSummaryState<SummaryFor<S>> => {
  type T = SummaryFor<S>;
  const kind = scope?.kind ?? null;
  const id = scope?.id ?? null;
  const enabled = Boolean(kind && id);
  const queryKey = summaryQueryKey(kind, id, windowDays);
  const [forcing, setForcing] = useState(false);
  const debounce = useRef<number | null>(null);

  const query = useQuery(
    {
      queryKey,
      queryFn: () =>
        DashboardAPI.getSummary(
          { kind: kind as DashboardScope["kind"], id: id as string },
          windowDays
        ) as Promise<T>,
      enabled,
      // Only the practice payload is restored from disk; a client's Overview
      // is cheap and changes with the client you open.
      meta: { persist: kind === "practice" }
    },
    queryClient
  );

  const scheduleRefresh = useCallback(() => {
    if (debounce.current) window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => {
      debounce.current = null;
      void queryClient.invalidateQueries({
        queryKey: summaryQueryKey(kind, id, windowDays)
      });
    }, SUMMARY_REFRESH_DEBOUNCE_MS);
  }, [kind, id, windowDays]);
  useEffect(
    () => () => {
      if (debounce.current) window.clearTimeout(debounce.current);
    },
    []
  );

  // Something happened in the scope: the numbers may have moved.
  useRealtimeEvent<{ foreign_national_id?: string }>(
    "activity.created",
    (ev) => {
      if (!kind || !id) return;
      if (kind === "client" && String(ev?.foreign_national_id ?? "") !== id)
        return;
      scheduleRefresh();
    }
  );

  useOnClientInvalidate(
    kind === "client" ? id : null,
    ["client", "cases", "documents", "intakes", "reminders", "records"],
    scheduleRefresh
  );

  const refresh = useCallback(async () => {
    if (!kind || !id) return;
    setForcing(true);
    try {
      const next = (await DashboardAPI.getSummary({ kind, id }, windowDays, {
        refresh: true
      })) as T;
      queryClient.setQueryData(summaryQueryKey(kind, id, windowDays), next);
    } finally {
      setForcing(false);
    }
  }, [id, kind, windowDays]);

  const data = (query.data as T | undefined) ?? null;
  return {
    data,
    isLoading: enabled && !data && query.isPending,
    isRefreshing: forcing || (Boolean(data) && query.isFetching),
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : "Couldn't load the dashboard."
      : null,
    refresh,
    updatedAt: data?.as_of ?? null
  };
};
